import { Box } from '@mui/system';
import { FC, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, Resolver, useForm } from 'react-hook-form';
import { Session } from '@supabase/supabase-js';

import { routes } from 'src/constants/routes';
import { StepList } from 'src/features/step-list';
import { StepEnum } from 'src/features/step-list/constants';
import { useFetchRoomByVitaRef } from 'src/lib/hooks/use-get-room-by-vita-ref';
import { createUserDetailsSchema } from 'src/features/details-form/user-details-schema';

import { Spinner } from '../../components/spinner';
import { UserDataFormValues } from '../../features/details-form/types';
import { UserDetailsPartial } from '../../auth/types';
import { useGetUserInfo } from '../../lib/hooks/use-get-user-info';
import { useGetUserHasBookings } from '../../lib/hooks/use-get-user-has-bookings';
import { DetailsForm } from '../../features/details-form';
import { useUpdateUserMetadata } from '../../lib/hooks/use-update-user-metadata';

import { styles } from './styles';
import { OccupantAgreementsContainer } from './occupant-agreements-container';

const LOGGED_IN = true;

export const OccupantDetailsContainer: FC<{ vitaRef: string; userSession: Session }> = ({
    vitaRef,
    userSession,
}) => {
    const [activeStep, setActiveStep] = useState<StepEnum>(StepEnum.YourDetails);
    const steps = [StepEnum.YourDetails, StepEnum.Agreements];

    const userDetails = userSession.user.user_metadata as UserDetailsPartial;

    const hideAgreements = () => setActiveStep(StepEnum.YourDetails);

    const {
        data,
        error,
        isPending: isFetchingRoomByVitaRef,
    } = useFetchRoomByVitaRef(vitaRef, LOGGED_IN);

    const {
        isPending: isUpdatingUserMetadata,
        isError: errorUpdatingUserMetadata,
        mutate: updateUserMetadata,
    } = useUpdateUserMetadata();

    const {
        data: userHasBookings,
        isLoading: isGettingUserBookings,
        isError: errorGettingUserBookings,
    } = useGetUserHasBookings(LOGGED_IN);

    const { data: userInfo } = useGetUserInfo(LOGGED_IN);

    const userDetailsSchema = createUserDetailsSchema(
        !LOGGED_IN,
        data?.building.spanish_tax_applicable || false
    );

    const methods = useForm<UserDataFormValues>({
        resolver: yupResolver(userDetailsSchema) as Resolver<UserDataFormValues>,
        defaultValues: {
            ...userDetails,
            nifNumber: userDetails?.nifNumber ?? userInfo?.nif_number ?? null,
            nieNumber: userDetails?.nieNumber ?? userInfo?.nie_number ?? null,
            passportNumber: userDetails?.passportNumber ?? userInfo?.passport_number ?? null,
            passportIssuingCountry:
                userDetails?.passportIssuingCountry ?? userInfo?.passport_issuing_country ?? null,
        },
        mode: 'all',
    });

    const { control, handleSubmit } = methods;

    if (error || errorGettingUserBookings || errorUpdatingUserMetadata) {
        return <Navigate to={routes.failure.index} />;
    }

    if (isFetchingRoomByVitaRef || isGettingUserBookings) {
        return (
            <Box css={styles.layout}>
                <Box css={styles.leftSide}>
                    <StepList activeStep={activeStep} steps={steps} paymentRequired={false} />
                    <Spinner size="big" />
                </Box>
            </Box>
        );
    }

    if (!data || !userHasBookings) {
        return <Navigate to={routes.failure.index} />;
    }

    const handleSubmitDetails = handleSubmit((values) => {
        updateUserMetadata(
            {
                userValues: values,
                isSpanishTaxApplicable: data.building.spanish_tax_applicable,
            },
            {
                onSuccess: () => setActiveStep(StepEnum.Agreements),
            }
        );
    });

    return (
        <FormProvider {...methods}>
            <Box css={styles.layout}>
                <Box css={styles.leftSide}>
                    <StepList
                        activeStep={activeStep}
                        steps={steps}
                        paymentRequired={data.room.advance_payment_weeks > 0}
                    />

                    {activeStep === StepEnum.YourDetails && (
                        <DetailsForm
                            control={control}
                            onFormSubmit={handleSubmitDetails}
                            hasRoommates={false}
                            loading={isUpdatingUserMetadata}
                            userHasBookings={userHasBookings ?? false}
                            userLoggedIn={LOGGED_IN}
                            building={data.building}
                        />
                    )}

                    {activeStep === StepEnum.Agreements && (
                        <OccupantAgreementsContainer
                            vitaRef={vitaRef}
                            building={data.building}
                            handleBackButton={hideAgreements}
                            academicYear={data.room.academic_year}
                        />
                    )}
                </Box>
            </Box>
        </FormProvider>
    );
};
